import type {
  DocEntityAssociationBaseHandle,
  DocEntityAssociationHandle,
} from "building-types/associations/associationHandleTypes";
import type {
  DocAssociationSourceEntityType,
  DocAssociationSourceTypeToTargetType,
  DocAssociationTargetEntityType,
} from "building-types/associations/associationTypes";
import type { Doc } from "building-types/doc";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { RWTable } from "common/data/table";
import type { DocEntityTypeToHandleType } from "../entity/docEntityTypeToHandleType";
import { docGetEntityHandleById } from "../entity/docGetEntityHandleById";
import { isAssociationSourceEntityHandle, isAssociationTargetEntityHandle } from "./docAssociationHandleTools";
import {
  ALL_ENTITY_ASSOCIATION_SOURCE_TYPES,
  ALL_ENTITY_ASSOCIATION_TARGET_TYPES,
  docForEachSourceEntityAssociation,
  docForEachTargetEntityAssociation,
  docForEachTargetEntityAssociationByType,
} from "./docForEachEntityAssociation";
import { docGetEntityAssociation } from "./docGetEntityAssociation";

export function docEntityGetTargetAssociations(
  doc: Doc,
  sourceHandle: DocEntityHandle,
  targetHandleTypes: DocAssociationTargetEntityType[] = ALL_ENTITY_ASSOCIATION_TARGET_TYPES,
  recursive: boolean = false,
  visited: RWTable<boolean> = {},
): DocEntityAssociationHandle[] {
  if (!isAssociationSourceEntityHandle(sourceHandle)) {
    return [];
  }

  const targetAssociations: DocEntityAssociationHandle[] = [];

  docForEachTargetEntityAssociation(
    doc,
    (association, handle) => {
      if (association.sourceEntityId === sourceHandle.id) {
        targetAssociations.push(handle);
      }
    },
    sourceHandle,
    targetHandleTypes,
    recursive,
    visited,
  );

  return targetAssociations;
}

export function docEntityGetSourceAssociations(
  doc: Doc,
  targetHandle: DocEntityHandle,
  sourceHandleTypes: DocAssociationSourceEntityType[] = ALL_ENTITY_ASSOCIATION_SOURCE_TYPES,
  recursive: boolean = false,
  visited: RWTable<boolean> = {},
): DocEntityAssociationHandle[] {
  if (!isAssociationTargetEntityHandle(targetHandle)) {
    return [];
  }

  const sourceAssociations: DocEntityAssociationHandle[] = [];

  docForEachSourceEntityAssociation(
    doc,
    (association, handle) => {
      if (association.targetEntityId === targetHandle.id) {
        sourceAssociations.push(handle);
      }
    },
    targetHandle,
    sourceHandleTypes,
    recursive,
    visited,
  );

  return sourceAssociations;
}

export function docGetTargetAssociatedEntities(
  doc: Doc,
  sourceHandle: DocEntityHandle,
  targetHandleTypes: DocAssociationTargetEntityType[] = ALL_ENTITY_ASSOCIATION_TARGET_TYPES,
  recursive: boolean = false,
  visited: RWTable<boolean> = {},
): DocEntityHandle[] {
  const targetAssociationHandles = docEntityGetTargetAssociations(
    doc,
    sourceHandle,
    targetHandleTypes,
    recursive,
    visited,
  );

  const targetAssociations = targetAssociationHandles
    .map((handle) => docGetEntityAssociation(doc, handle))
    .filter((association) => !!association)
    .sort((a, b) => a.sortIndex - b.sortIndex);

  return targetAssociations
    .map((association) => docGetEntityHandleById(doc, association.targetEntityId, targetHandleTypes))
    .filter((handle) => !!handle);
}

export function docGetSourceAssociatedEntities(
  doc: Doc,
  targetHandle: DocEntityHandle,
  sourceHandleTypes: DocAssociationSourceEntityType[] = ALL_ENTITY_ASSOCIATION_SOURCE_TYPES,
  recursive: boolean = false,
  visited: RWTable<boolean> = {},
): DocEntityHandle[] {
  const sourceAssociationHandles = docEntityGetSourceAssociations(
    doc,
    targetHandle,
    sourceHandleTypes,
    recursive,
    visited,
  );

  const sourceAssociations = sourceAssociationHandles
    .map((handle) => docGetEntityAssociation(doc, handle))
    .filter((association) => !!association)
    .sort((a, b) => a.sortIndex - b.sortIndex);

  return sourceAssociations
    .map((association) => docGetEntityHandleById(doc, association.sourceEntityId, sourceHandleTypes))
    .filter((handle) => !!handle);
}

export function docEntityGetTargetAssociationsByType<
  S extends DocAssociationSourceEntityType,
  T extends DocAssociationSourceTypeToTargetType[S],
>(doc: Doc, sourceHandle: DocEntityHandle, targetHandleType: T): DocEntityAssociationBaseHandle<S, T>[] {
  if (!isAssociationSourceEntityHandle(sourceHandle)) {
    return [];
  }

  const targetAssociations: DocEntityAssociationBaseHandle<S, T>[] = [];

  docForEachTargetEntityAssociationByType(
    doc,
    (association, handle) => {
      if (association.sourceEntityId === sourceHandle.id) {
        targetAssociations.push(handle as DocEntityAssociationBaseHandle<S, T>);
      }
    },
    sourceHandle,
    targetHandleType,
  );

  return targetAssociations;
}

export function docGetTargetAssociatedEntitiesByType<T extends DocAssociationTargetEntityType>(
  doc: Doc,
  sourceHandle: DocEntityHandle,
  targetHandleType: T,
): DocEntityTypeToHandleType[T][] {
  if (!isAssociationSourceEntityHandle(sourceHandle)) {
    return [];
  }

  const targetAssociationHandles = docEntityGetTargetAssociationsByType(doc, sourceHandle, targetHandleType);

  const targetAssociations = targetAssociationHandles
    .map((handle) => docGetEntityAssociation(doc, handle))
    .filter((association) => !!association)
    .sort((a, b) => a.sortIndex - b.sortIndex);

  return targetAssociations
    .map(
      (association) =>
        docGetEntityHandleById(doc, association.targetEntityId, [targetHandleType]) as DocEntityTypeToHandleType[T],
    )
    .filter((handle) => !!handle);
}
